import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import PortalRegisterModal from "../components/PortalRegisterModal";
import SiteHeader from "../components/SiteHeader";
import { getPastLabs, getUpcomingLabs } from "../firebase";
import { buildCalendarLinks } from "../utils/calendar";
import { formatLabDate, formatLabTimeRange } from "../utils/datetime";
import { buildResourceSummary } from "../utils/resources";

const HUB_SOURCE = {
  sourceType: "hub",
  sourceSlug: "school-leader-ai-lab-hub",
  sourceName: "School Leader AI Lab Hub",
};

const HUB_SECTIONS = [
  {
    id: "hub-upcoming",
    label: "1) Upcoming sessions",
    helper: "Start here",
  },
  {
    id: "hub-past",
    label: "2) Past recordings",
    helper: "Then review",
  },
  {
    id: "hub-resources",
    label: "3) Resources",
    helper: "Grab links fast",
  },
];

const renderRecordingAction = (lab) => {
  if (!lab.youtubeId) {
    return <span className="portal-disabled-action">Recording coming soon</span>;
  }

  return (
    <Link
      className="btn btn-outline"
      to={`/lab/${lab.id}`}
      state={{ hubMode: true, backTo: "/hub" }}
    >
      Watch recording
    </Link>
  );
};

const renderResourcesAction = (lab) => {
  if (!Array.isArray(lab.resources) || lab.resources.length === 0) {
    return <span className="portal-disabled-action">Resources coming soon</span>;
  }

  return (
    <Link
      className="btn btn-subtle"
      to={{ pathname: `/lab/${lab.id}`, hash: "#resources" }}
      state={{ hubMode: true, backTo: "/hub" }}
    >
      Open resources
    </Link>
  );
};

const renderAddToCalendarAction = (lab) => {
  try {
    const { googleUrl } = buildCalendarLinks(lab);

    return (
      <a
        className="btn btn-primary portal-add-calendar-btn"
        href={googleUrl}
        target="_blank"
        rel="noreferrer"
      >
        Add to calendar
      </a>
    );
  } catch (error) {
    console.error("Failed to build calendar link for lab card:", error);
    return null;
  }
};

function HubPage() {
  const [upcomingLabs, setUpcomingLabs] = useState([]);
  const [pastLabs, setPastLabs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState(HUB_SECTIONS[0].id);

  useEffect(() => {
    let isActive = true;

    const loadLabs = async () => {
      setIsLoading(true);
      setError("");

      try {
        const [upcomingResponse, pastResponse] = await Promise.all([
          getUpcomingLabs(8),
          getPastLabs(30),
        ]);

        if (isActive) {
          setUpcomingLabs(upcomingResponse);
          setPastLabs(pastResponse);
        }
      } catch (loadError) {
        console.error("Failed to load hub labs:", loadError);
        if (isActive) {
          setError("We couldn't load the hub data right now. Please refresh in a moment.");
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadLabs();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    const sectionElements = HUB_SECTIONS.map((section) =>
      document.getElementById(section.id)
    ).filter(Boolean);

    if (sectionElements.length === 0) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((left, right) => right.intersectionRatio - left.intersectionRatio);

        if (visibleEntries.length > 0) {
          setActiveSectionId(visibleEntries[0].target.id);
        }
      },
      {
        rootMargin: "-35% 0px -45% 0px",
        threshold: [0.2, 0.4, 0.6],
      }
    );

    sectionElements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  const allLabs = useMemo(() => [...upcomingLabs, ...pastLabs], [upcomingLabs, pastLabs]);
  const resourceSummary = useMemo(() => buildResourceSummary(allLabs), [allLabs]);
  const resourcePreview = resourceSummary.dedupedResources.slice(0, 10);
  const nextLab = upcomingLabs[0] || null;

  const openRegister = () => setIsRegisterOpen(true);
  const closeRegister = () => setIsRegisterOpen(false);

  const renderLabCard = (lab, options = {}) => {
    const { isUpcoming = false } = options;
    const cardClassName = `card portal-lab-card ${
      isUpcoming ? "portal-lab-card-upcoming" : ""
    }`;

    return (
      <article key={lab.id} className={cardClassName.trim()}>
        <p className="eyebrow">{formatLabDate(lab.dateTime)}</p>
        <p className="lab-time">{formatLabTimeRange(lab.dateTime, lab.durationMinutes)}</p>
        <h3>{lab.title}</h3>
        <p>{lab.shortDescription}</p>

        <div
          className={`action-row portal-action-row ${
            isUpcoming ? "portal-action-row-upcoming" : ""
          }`}
        >
          {isUpcoming ? renderAddToCalendarAction(lab) : null}
          <Link
            className={isUpcoming ? "btn btn-outline" : "btn btn-primary"}
            to={`/lab/${lab.id}`}
            state={{ hubMode: true, backTo: "/hub" }}
          >
            View details
          </Link>
          {!isUpcoming ? renderRecordingAction(lab) : null}
          {renderResourcesAction(lab)}
        </div>

        <button className="portal-register-inline" type="button" onClick={openRegister}>
          Not registered yet? Register here
        </button>
      </article>
    );
  };

  return (
    <>
      <SiteHeader
        registerLabel="Add next AI Lab to my calendar"
        onRegisterClick={openRegister}
      />

      <main className="portal-main">
        <section className="hero section portal-hero">
          <div className="container">
            <h1>School Leader AI Lab Hub</h1>
            <p className="hero-copy portal-hero-copy">
              One clean place to find upcoming labs, past recordings, and resources
              from every published session.
            </p>
<div className="portal-nav-shell">
              <p className="portal-nav-heading">Jump to what you need</p>
              <nav className="portal-section-nav" aria-label="Hub sections">
                {HUB_SECTIONS.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className={`portal-nav-btn`}
                    aria-current={activeSectionId === section.id ? "location" : undefined}
                  >
                    <span className="portal-nav-hint">{section.helper}</span>
                    <span className="portal-nav-label">{section.label}</span>
                  </a>
                ))}
              </nav>
            </div>
            {nextLab ? (
              <aside className="card hub-next-session-card" aria-label="Next session">
                <p className="eyebrow">Next session</p>
                <h2>{nextLab.title}</h2>
                <p className="lab-time">
                  {formatLabDate(nextLab.dateTime)} ·{" "}
                  {formatLabTimeRange(nextLab.dateTime, nextLab.durationMinutes)}
                </p>
                <p className="muted-copy">
                  Add it to your calendar now so you never miss the live session.
                </p>
                <button
                  className="btn btn-primary hub-next-session-cta"
                  type="button"
                  onClick={openRegister}
                >
                  Add next AI Lab to my calendar
                </button>
              </aside>
            ) : null}

            
          </div>
        </section>

        {error ? (
          <section className="section">
            <div className="container">
              <p className="status-message error">{error}</p>
            </div>
          </section>
        ) : null}

        <section id="hub-upcoming" className="section portal-section-target">
          <div className="container">
            <h2>Upcoming sessions</h2>
            <p className="muted-copy">Know what's next and get prepared in a few clicks.</p>

            {isLoading ? <p>Loading upcoming sessions...</p> : null}
            {!isLoading && upcomingLabs.length === 0 ? (
              <p>No upcoming sessions are published yet.</p>
            ) : null}

            <div className="portal-labs-grid">
              {!isLoading
                ? upcomingLabs.map((lab) => renderLabCard(lab, { isUpcoming: true }))
                : null}
            </div>
          </div>
        </section>

        <section id="hub-past" className="section section-alt portal-section-target">
          <div className="container">
            <h2>Past recordings</h2>
            <p className="muted-copy">Replay past sessions and revisit what matters.</p>

            {isLoading ? <p>Loading past recordings...</p> : null}
            {!isLoading && pastLabs.length === 0 ? <p>No past recordings yet.</p> : null}

            <div className="portal-labs-grid">
              {!isLoading ? pastLabs.map((lab) => renderLabCard(lab)) : null}
            </div>
          </div>
        </section>

        <section id="hub-resources" className="section portal-section-target">
          <div className="container">
            <div className="portal-resources-header">
              <div>
                <h2>Resources</h2>
                <p className="muted-copy">
                  Quick-access links from published labs, with duplicates removed.
                </p>
              </div>
              <Link className="btn btn-primary" to="/resources">
                Open full resources page
              </Link>
            </div>

            {resourcePreview.length === 0 ? (
              <p>Resources will appear here as labs are published.</p>
            ) : (
              <div className="portal-resource-grid">
                {resourcePreview.map((resource) => (
                  <article key={resource.id} className="card portal-resource-card">
                    <h3>{resource.label}</h3>
                    <p className="muted-copy">
                      Used in {resource.usageCount}{" "}
                      {resource.usageCount === 1 ? "session" : "sessions"}
                    </p>
                    <a href={resource.url} target="_blank" rel="noreferrer">
                      Open resource
                    </a>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <PortalRegisterModal
        isOpen={isRegisterOpen}
        onClose={closeRegister}
        nextLab={nextLab}
        source={HUB_SOURCE}
        title="Add next AI Lab to my calendar"
        description="Enter your info once to add the next AI Lab to your calendar and receive monthly Zoom details and recordings."
        submitLabel="Add next AI Lab to my calendar"
      />

      <Footer />
    </>
  );
}

export default HubPage;
